import { Maybe, PaymentMethod, PaymentMethodType } from '@css-xp/graphtypes';
import { PaymentOnTerms } from '@css-xp/graphtypes/src/generated-types-new';
import { ENABLE_CREDIT_MANAGEMENT } from '@atlassian/cssxp-utils-generic';
import { InfoPanel, useFeatureToggle } from '@css-xp/base';
import { AdditionalPaymentMethods } from '@css-xp/base/additional-payment-methods';
import { Divider } from '@css-xp/base/divider';
import { GeneratePaymentLink } from '@css-xp/base/generate-payment-link';
import { PaymentMethodsTableV2 } from '@css-xp/base/payment-methods-table-v2';
import React, { useEffect, useMemo, useState } from 'react';

interface PaymentMethodStepProps {
  availablePaymentMethods?: Maybe<Maybe<PaymentMethod>[]> | undefined;
  updatePaymentMethodLink?: Maybe<string>;
  onFieldValueChange: (name: string, value: any) => void;
  selectedPaymentId?: string;
  termPaymentStatus?: Maybe<string> | null;
}

export const PaymentMethodStep = ({
  availablePaymentMethods,
  updatePaymentMethodLink,
  onFieldValueChange,
  selectedPaymentId,
  termPaymentStatus,
}: PaymentMethodStepProps) => {
  const [isEnabled] = useFeatureToggle();
  const isCreditManagementEnabled = isEnabled(ENABLE_CREDIT_MANAGEMENT);
  const deferredPaymentMethods = useMemo(() => {
    return availablePaymentMethods?.filter((paymentMethod) => paymentMethod?.type === PaymentMethodType.DEFERRED) ?? [];
  }, [availablePaymentMethods]);

  const cardPaymentMethods = useMemo(() => {
    return availablePaymentMethods?.filter((paymentMethod) => paymentMethod?.type === PaymentMethodType.CARD) ?? [];
  }, [availablePaymentMethods]);

  const defaultPaymentMethodId = useMemo(() => {
    const defaultPaymentMethod = availablePaymentMethods?.find((paymentMethod) => paymentMethod?.default);
    return defaultPaymentMethod?.id ?? '';
  }, [availablePaymentMethods]);

  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string>(defaultPaymentMethodId);

  useEffect(() => {
    onFieldValueChange('defaultPaymentMethod', selectedPaymentMethodId);
  }, [onFieldValueChange, selectedPaymentMethodId]);

  useEffect(() => {
    if (selectedPaymentId) {
      setSelectedPaymentMethodId(selectedPaymentId);
    }
  }, [selectedPaymentId]);

  const handleSelectPaymentMethod = (id: string) => {
    setSelectedPaymentMethodId(id);
  };

  const isTermPaymentAvailable = deferredPaymentMethods.length > 0;

  const isDefaultMethodIsCard = cardPaymentMethods?.some(
    (paymentMethod) => paymentMethod?.id === defaultPaymentMethodId
  );

  const paymentMethodsTableProps = {
    cardPaymentMethods: cardPaymentMethods as PaymentMethod[],
    defaultPaymentMethodId,
    selectPaymentMethod: handleSelectPaymentMethod,
    selectedPaymentMethodId,
    header: isCreditManagementEnabled ? 'Select from existing methods' : 'Select existing payment method',
    termPaymentMethods: deferredPaymentMethods as PaymentOnTerms[],
    isCreditManagementEnabled,
    isTermPaymentAvailable,
    isDefaultMethodIsCard,
    termPaymentStatus,
  };

  return (
    <div>
      <div className="heading">
        <h2>Payment method</h2>
      </div>
      <GeneratePaymentLink updatePaymentMethodLink={updatePaymentMethodLink} stacked />
      <Divider />
      <div className="term-payment-info-container">
        <InfoPanel
          type="warning"
          title="Select a payment method"
          message="You won't be able to switch to a payment term later if you select any other method right now"
        />
      </div>
      <PaymentMethodsTableV2 {...paymentMethodsTableProps} />
      {!isCreditManagementEnabled && (
        <>
          <Divider />
          <AdditionalPaymentMethods
            defaultPaymentMethodId={selectedPaymentId ?? defaultPaymentMethodId}
            deferredPaymentMethods={deferredPaymentMethods}
            isTermPaymentAvailable={isTermPaymentAvailable}
            selectPaymentMethod={handleSelectPaymentMethod}
            selectedPaymentMethodId={selectedPaymentMethodId}
          />
        </>
      )}
    </div>
  );
};
